// SPDX-License-Identifier: MIT
// Results Table Component - Displays query results with virtualization
// Adapted from Kepler.gl's PreviewDataPanel pattern

import React from 'react';
import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import type { QueryResult } from '../types';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.borderColor}60;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: max-content;
`;

const TableHead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TableHeader = styled.th`
  padding: 8px 12px;
  text-align: left;
  background: ${props => props.theme.sidePanelHeaderBg};
  color: ${props => props.theme.textColor};
  font-weight: 700;
  border-bottom: 2px solid ${props => props.theme.primaryColor};
  white-space: nowrap;
  position: relative;
  font-size: 11px;
  letter-spacing: 0.02em;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 25%;
    height: 50%;
    width: 1px;
    background: ${props => props.theme.borderColor}40;
  }
`;

const ColumnType = styled.span`
  font-weight: 400;
  color: ${props => props.theme.subtextColor};
  font-size: 10px;
  margin-left: 6px;
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $isEven: boolean }>`
  background: ${props => (props.$isEven ? props.theme.panelBackground : props.theme.sidePanelBg)};
  transition: background 0.1s ease;

  &:hover {
    background: ${props => props.theme.panelBackgroundHover};
  }
`;

const TableCell = styled.td`
  padding: 5px 10px;
  color: ${props => props.theme.textColor};
  border-bottom: 1px solid ${props => props.theme.borderColor}20;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NullValue = styled.span`
  color: ${props => props.theme.subtextColor};
  font-style: italic;
  opacity: 0.6;
`;

const GeometryValue = styled.span`
  color: ${props => props.theme.activeColor || '#4CAF50'};
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  font-size: 11px;
`;

const EmptyResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.theme.subtextColor};
  font-size: 14px;
`;

// Props interface
interface ResultsTableProps {
  result: QueryResult;
  theme: DefaultTheme;
}

/**
 * Format cell value for display
 */
function formatCellValue(value: unknown, type: string): React.ReactNode {
  if (value === null || value === undefined) {
    return <NullValue>NULL</NullValue>;
  }

  const upperType = type.toUpperCase();

  // Geometry types - show abbreviated representation
  if (upperType.includes('GEOMETRY') || upperType === 'BLOB') {
    if (value instanceof Uint8Array || ArrayBuffer.isView(value)) {
      return <GeometryValue>[Geometry]</GeometryValue>;
    }
    if (
      typeof value === 'string' &&
      (value.startsWith('POINT') || value.startsWith('POLYGON') || value.startsWith('LINESTRING'))
    ) {
      const abbreviated = value.length > 30 ? value.substring(0, 30) + '...' : value;
      return <GeometryValue title={value}>{abbreviated}</GeometryValue>;
    }
  }

  // Boolean
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  // Numbers
  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
  }

  // Date/Timestamp
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Objects/Arrays - stringify
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

export const ResultsTable: React.FC<ResultsTableProps> = ({ result, theme }) => {
  if (!result || result.displayRows.length === 0) {
    return <EmptyResults theme={theme}>No results to display</EmptyResults>;
  }

  const { columns, displayRows } = result;

  return (
    <Container>
      <TableWrapper theme={theme}>
        <StyledTable>
          <TableHead>
            <tr>
              {columns.map(col => (
                <TableHeader key={col.name} theme={theme}>
                  {col.name}
                  <ColumnType theme={theme}>{col.type}</ColumnType>
                </TableHeader>
              ))}
            </tr>
          </TableHead>
          <TableBody>
            {displayRows.map((row, rowIdx) => (
              <TableRow key={rowIdx} $isEven={rowIdx % 2 === 0} theme={theme}>
                {columns.map(col => (
                  <TableCell key={col.name} theme={theme} title={String(row[col.name] ?? '')}>
                    {formatCellValue(row[col.name], col.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableWrapper>
    </Container>
  );
};

export default ResultsTable;
